import React, { useState, useEffect, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { useMutation, useQuery } from '@apollo/client';
import { DndProvider } from 'react-dnd';
import { TouchBackend } from 'react-dnd-touch-backend';
import { createDragDropManager } from 'dnd-core';
import update from 'immutability-helper';
import { orderBy } from 'lodash';
import PropdocsModal from '../../modal/PropdocsModal';
import LoadingSpinner from '../../loading-spinner';
import { useNotifications } from '../../../notifications';
import { mutations } from '../../../api';
import { getDocumentSigners, getDocumentById } from '../../../api/queries_v2';
import styles from './UpdateSignersModal.module.scss';
import { UpdateSignerItem } from './UpdateSignerItem';

// Nested DndProvider would reuse the app's HTML5 manager; mobile needs its own touch manager
const touchDndManager = createDragDropManager(TouchBackend, undefined, {
  enableMouseEvents: true,
  delayTouchStart: 120,
});

interface Signer {
  id: string;
  email: string;
  order: number;
  signedAt?: string | null;
}

interface UpdateSignersModalProps {
  show: boolean;
  onHide: () => void;
  documentId: string;
}

export const UpdateSignersModal: React.FC<UpdateSignersModalProps> = ({
  show,
  onHide,
  documentId,
}) => {
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const [localSigners, setLocalSigners] = useState<Signer[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSigners, setOriginalSigners] = useState<Signer[]>([]);

  // Fetch current signers
  const {
    data,
    loading: loadingSigners,
    refetch,
  } = useQuery(getDocumentSigners, {
    variables: { documentId },
    skip: !documentId || !show,
    fetchPolicy: 'network-only',
  });

  // Fetch document details to get useSignerOrder
  const { data: documentData, loading: loadingDocument } = useQuery(getDocumentById, {
    variables: { documentId },
    skip: !documentId || !show,
    fetchPolicy: 'cache-first',
  });

  // Update signers mutation
  const [updateSigners, { loading: updating }] = useMutation(mutations.updateSignersDuringSigning, {
    onCompleted: () => {
      notifySuccess({ message: 'Signers updated successfully' });
      refetch();
      onHide();
    },
    onError: (error) => {
      notifyError({ message: error.message || 'Failed to update signers' });
    },
  });

  // Load signers when data arrives
  useEffect(() => {
    if (data?.getDocumentById?.signers) {
      const sorted = orderBy(data.getDocumentById.signers, 'order');
      setLocalSigners(sorted);
      setOriginalSigners(sorted);
      setHasChanges(false);
    }
  }, [data]);

  // Get useSignerOrder from document data
  const useSignerOrder = Boolean((documentData?.getDocumentById as any)?.useSignerOrder);

  // Check for changes
  useEffect(() => {
    const hasEmailChanges = localSigners.some((signer, index) => {
      const original = originalSigners.find((o) => o.id === signer.id);
      return original && (original.email !== signer.email || original.order !== index);
    });
    setHasChanges(hasEmailChanges);
  }, [localSigners, originalSigners]);

  // Handle drag and drop reordering
  const moveItem = useCallback((dragIndex: number, hoverIndex: number) => {
    setLocalSigners((prevSigners) =>
      update(prevSigners, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevSigners[dragIndex]],
        ],
      })
    );
  }, []);

  // Handle email update
  const updateSignerEmail = useCallback(async (id: string, email: string) => {
    setLocalSigners((prevSigners) => prevSigners.map((s) => (s.id === id ? { ...s, email } : s)));
  }, []);

  // Handle save
  const handleSave = async () => {
    const signersInput = localSigners.map((signer, index) => ({
      id: signer.id,
      email: signer.email,
      order: index,
    }));

    await updateSigners({
      variables: {
        documentId,
        signers: signersInput,
      },
    });
  };

  // Determine which signers can be edited
  const getSignerStatus = (signer: Signer) => {
    if (signer.signedAt) {
      return { status: 'signed', canEdit: false, badge: '✓ Signed' };
    }

    // Check if signer has been notified (has active session)
    // For now, we'll check this on the backend
    // In a real implementation, we'd want to fetch session status
    const original = originalSigners.find((o) => o.id === signer.id);
    const emailChanged = original && original.email !== signer.email;

    return {
      status: 'pending',
      canEdit: true,
      badge: emailChanged ? '• Modified' : '• Pending',
    };
  };

  const isLoading = loadingSigners || loadingDocument;

  return (
    <PropdocsModal show={show} setShow={onHide} size="md" title={<span>Update Signers</span>}>
      <div className={styles.modalContent}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <LoadingSpinner />
          </div>
        ) : (
          <DndProvider manager={touchDndManager}>
            <div className={styles.signersList}>
              {localSigners.map((signer, index) => {
                const signerInfo = getSignerStatus(signer);
                return (
                  <UpdateSignerItem
                    key={signer.id}
                    id={signer.id}
                    index={index}
                    order={index}
                    email={signer.email}
                    moveCard={moveItem}
                    updateSigner={updateSignerEmail}
                    canEdit={signerInfo.canEdit}
                    statusBadge={signerInfo.badge}
                    isDraggable={useSignerOrder && !signer.signedAt}
                    showOrderNumber={useSignerOrder}
                  />
                );
              })}
            </div>
          </DndProvider>
        )}

        {hasChanges && (
          <div className={styles.changesNotice}>
            <small>Updated signers will receive new invitation emails</small>
          </div>
        )}

        <div className={styles.modalFooter}>
          <Button variant="secondary" onClick={onHide} disabled={updating}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!hasChanges || updating}
            className={styles.primaryButton}
            title={!hasChanges ? 'Make changes to enable this button' : ''}
          >
            {updating ? 'Updating...' : 'Update & Send Invitations'}
          </Button>
        </div>
      </div>
    </PropdocsModal>
  );
};
